import {List} from 'immutable';
import {Seconds} from '@store/@types/util';

export type SharedAreas = List<[number, number]>;

/**
 * Determine if the given time is in a valid part of the call
 */
export function isValidTime(sharedAreas: SharedAreas, time: Seconds): boolean {
  for (const [start, end] of sharedAreas) {
    if (time >= start && time <= end) {
      return true;
    }
  }
  return false;
}

/**
 * Get the next valid time.
 * If the given time is valid, then return that
 * If the rest of the call is not available, then return effectiveEnd
 */
export function getNextValidTime(sharedAreas: SharedAreas, currentTime: Seconds): Seconds {
  const effectiveEnd = sharedAreas.map(([_, end]) => end).max();
  // If we're past the playable region, stop there
  if (currentTime >= effectiveEnd) {
    return effectiveEnd;
  }
  for (const [start, end] of sharedAreas) {
    // If we're in range then return that value
    if (currentTime >= start && currentTime <= end) {
      return currentTime;
    }
    // Otherwise return the start of the nearest valid area
    if (currentTime <= start) {
      return start;
    }
  }

  // This shouldn't happen, but to keep typescript happy return effectiveEnd
  return effectiveEnd;
}

/**
 * @VisibleForTesting
 */
export function joinSharedAreas(areas: SharedAreas): SharedAreas {
  if (areas.isEmpty()) return areas;

  // Sort by start time
  const sortedAreas = areas.sortBy(([start, _end]) => start);
  // List of joined areas
  let joinedList = List<[number, number]>();
  // The pair that we're actively joining
  let [currentStart, currentEnd] = sortedAreas.first();
  // Flag for whether we need to continue recursion
  let overlapFound = false;

  for (const [start, end] of sortedAreas.rest()) {
    if (start <= currentEnd) {
      // Overlap found, combine them by taking the latest end value
      // currentStart is always less, since we sorted by that at the beginning.
      currentEnd = Math.max(currentEnd, end);
      overlapFound = true;
    } else {
      // No overlap, add current area to the joined list and continue with this area
      joinedList = joinedList.push([currentStart, currentEnd]);
      currentStart = start;
      currentEnd = end;
    }
  }

  // Add the final area
  joinedList = joinedList.push([currentStart, currentEnd]);

  if (overlapFound) {
    // continue combining overlaps
    return joinSharedAreas(joinedList);
  } else {
    // no more overlaps to combine
    return joinedList;
  }
}

/**
 * Zip highlights to match ends with the following start
 * We're guaranteed to not have overlaps here thanks to
 * @link joinSharedAreas
 *
 * ex:
 * duration = 400
 * sharedAreas = [
 *   [10, 100],
 *   [120, 300],
 * ]
 * starts = [10, 120]
 * ends = [100, 300]
 *
 * We add 0 to the beginning to create a block from the start of the call to the first highlight
 * We also add duration to the end to create a block from the final highlight to the end of the call
 * This looks like this
 * starts = [10, 120, 400]
 * ends = [0, 100, 300]
 *
 * We zip these together which takes the elements of each list and pairs them together
 * zippedAreas = [
 *   [0, 10],
 *   [100, 120],
 *   [300, 400],
 * ]
 *
 * Then we use these pairs to render grey over the parts of the call that weren't explicitly shared
 *
 */
export function getInvalidAreas(sharedAreas: SharedAreas, duration: Seconds): SharedAreas {
  if (sharedAreas.isEmpty()) return sharedAreas;

  const starts = List([...sharedAreas.map(a => a[0]), duration]);
  const ends = List([0, ...sharedAreas.map(a => a[1])]);
  return ends.zip(starts);
}
